import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Settings, SettingsUpdatePayload } from '~/types/settings';

export type {
    Branding,
    Deployment,
    Firewall,
    FirewallProvider,
    Hytale,
    Injector,
    Misc,
    OpenGraph,
    Pusher,
    Settings,
    SettingsUpdatePayload,
    SMTP,
    WHMCS,
} from '~/types/settings';

type SettingsRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is SettingsRecord => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const useSettingsStore = defineStore('settings', () => {
    // State
    const initialized = ref<boolean>(false);
    const data = ref<Settings | undefined>(undefined);

    // Getters
    const isInitialized = computed(() => initialized.value);
    const settings = computed(() => data.value);
    const branding = computed(() => data.value?.branding);
    const features = computed(() => data.value?.features || {});
    const addons = computed(() => data.value?.addons || []);
    const whmcs = computed(() => data.value?.whmcs);
    const smtp = computed(() => data.value?.smtp);
    const openGraph = computed(() => data.value?.og);
    const injector = computed(() => data.value?.injector);
    const misc = computed(() => data.value?.misc);
    const pusher = computed(() => data.value?.pusher);
    const defaultLocale = computed(() => data.value?.default_locale);
    const isExpired = computed(() => data.value?.expired || false);

    // Feature helpers
    const hasFeature = computed(() => (featureName: string) => {
        return !!features.value[featureName]?.available;
    });

    const getFeature = computed(() => (featureName: string) => {
        return features.value[featureName];
    });

    const isPhysgun = computed(() => data.value?.addons?.some((addon) => addon.name === 'physgun') ?? false);
    const isPhysgunSharedHosting = computed(() => {
        if (!isPhysgun.value) return false;
        const host = window.location.hostname;
        // Production shared-hosting domain + the dev/test variant.
        // Mirrors the daemon-side gate in physgun.IsSharedHosting()
        // so panel dev hosts get the same UI features as prod.
        return host === 'gamecp.physgun.com' || host === 'physgun.panel.wisp.infraly.co' || host.endsWith('.test');
    });
    const hasFirewallProviders = computed(() => (data.value?.firewall?.providers?.length ?? 0) > 0);

    // Branding helpers
    const siteName = computed(() => branding.value?.name || 'Wisp');
    const siteLogo = computed(() => branding.value?.logo);
    const loginLogo = computed(() => branding.value?.login_logo);
    const favicon = computed(() => branding.value?.favicon);

    // Actions
    const markInitialized = () => {
        initialized.value = true;
    };

    const setSettings = (newSettings?: Settings) => {
        data.value = newSettings;
    };

    const updateSettings = (payload: SettingsUpdatePayload) => {
        if (!data.value) return;

        const deepUpdate = (target: SettingsRecord, source: SettingsRecord) => {
            for (const key in source) {
                const value = source[key];

                if (isRecord(value)) {
                    if (!isRecord(target[key])) {
                        target[key] = {};
                    }

                    deepUpdate(target[key] as SettingsRecord, value);
                } else {
                    target[key] = value;
                }
            }
        };

        deepUpdate(data.value as unknown as SettingsRecord, payload as unknown as SettingsRecord);
    };

    const clearSettings = () => {
        initialized.value = false;
        data.value = undefined;
    };

    // Initialize with empty features and addons if not provided
    const initializeDefaults = () => {
        if (!data.value) {
            data.value = {
                features: {},
                addons: [],
            };
        }
    };

    return {
        // State
        initialized,
        data,

        // Getters
        isInitialized,
        settings,
        branding,
        features,
        addons,
        whmcs,
        smtp,
        openGraph,
        injector,
        misc,
        pusher,
        defaultLocale,
        isExpired,

        // Feature helpers
        hasFeature,
        getFeature,

        // Addons helpers
        isPhysgun,
        isPhysgunSharedHosting,

        // Firewall helpers
        hasFirewallProviders,

        // Branding helpers
        siteName,
        siteLogo,
        loginLogo,
        favicon,

        // Actions
        markInitialized,
        setSettings,
        updateSettings,
        clearSettings,
        initializeDefaults,
    };
});

// Export types for external use
export type SettingsStore = ReturnType<typeof useSettingsStore>;
